import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Save, Loader2, Eye, EyeOff } from "lucide-react";
import { changePassword } from "@/lib/api";
import { toast } from "sonner";

function PwField({ label, value, onChange, testid, show }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border-slate-300 focus-visible:ring-[#0B2545]"
        data-testid={testid}
      />
    </div>
  );
}

export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) {
      toast.error("Mohon lengkapi semua kolom");
      return;
    }
    if (next.length < 8) {
      toast.error("Kata sandi baru minimal 8 karakter");
      return;
    }
    if (next !== confirm) {
      toast.error("Konfirmasi kata sandi tidak cocok");
      return;
    }
    if (next === current) {
      toast.error("Kata sandi baru harus berbeda dari yang lama");
      return;
    }
    setSaving(true);
    try {
      await changePassword(current, next);
      toast.success("Kata sandi berhasil diperbarui");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Gagal memperbarui kata sandi");
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="change-password-card">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50">
        <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#C59B27]" /> Ganti Kata Sandi Admin
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <PwField label="Kata Sandi Lama" value={current} onChange={setCurrent}
                     testid="change-password-current" show={show} />
          </div>
          <PwField label="Kata Sandi Baru (min. 8 karakter)" value={next} onChange={setNext}
                   testid="change-password-new" show={show} />
          <PwField label="Konfirmasi Kata Sandi Baru" value={confirm} onChange={setConfirm}
                   testid="change-password-confirm" show={show} />

          <div className="md:col-span-2 flex items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#0B2545]"
              data-testid="change-password-toggle-visibility"
            >
              {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {show ? "Sembunyikan" : "Tampilkan"} kata sandi
            </button>
            <Button type="submit" disabled={saving} className="btn-primary" data-testid="change-password-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Simpan Kata Sandi Baru
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
